// Pre-PO staged-shipping → PO pre-population (SO→PO rebuild Round 1, T08/T09).
//
// derivePosFromOrder (services/purchase-orders-builder.ts) groups an order's
// product lines by vendor and mints one PO per vendor. Round 1 adds a staging
// layer (line_item_shipping) the rep fills on the Shipping tab. This module is
// the PURE, DB-free decision that maps a vendor group's staged rows onto the
// supplier PO's header fields, so:
//   - the builder pre-populates address/account/method/ship-date/notes from the
//     staged rows instead of always falling back to the order defaults;
//   - an UNSTAGED group falls back to today's behavior BYTE-for-byte (usedStaging
//     false ⇒ every override is null and the caller keeps its fallback values);
//   - a group whose staged rows DISAGREE resolves first-by-position (a stable
//     winner) and records a conflict note the builder appends to internal_notes;
//   - the offline QA gate EXECUTES this exact decision (byte-parity, conflict,
//     first-by-position) without a database.
//
// It touches ONLY shipping/routing/notes fields — never cost or quantity. The
// decoration_service (two-leg) PO is assembled in the builder itself (it needs
// the merch'd bill-to constant + the DB write), but the winner it keys on comes
// from here (hasDecoratorLeg + winner).

export type PoShipRouting =
  | "client"
  | "decorator"
  | "merchd"
  | "other_supplier";
export type PoFinalShipTo = "client" | "merchd";

/** One line_item_shipping row as the PO builder reads it — only the fields that
 *  pre-populate a PO. Address snapshots stay opaque (jsonb). */
export interface StagedShippingRow {
  lineItemId: string;
  /** lineItems.position — first-by-position wins when a group's rows conflict. */
  position: number;
  shipToRouting: PoShipRouting;
  supplierShipToAddressId: string | null;
  supplierShipToAddressSnapshot: unknown;
  supplierShippingAccountId: string | null;
  supplierShippingMethod: string | null;
  supplierShipDate: string | null;
  supplierNotes: string | null;
  // ── decorator leg (used when shipToRouting = 'decorator') ──
  decoratorVendorId: string | null;
  decoratorAddressId: string | null;
  decoratorAddressSnapshot: unknown;
  decoratorFinalShipTo: PoFinalShipTo | null;
  decoratorFinalAddressId: string | null;
  decoratorFinalAddressSnapshot: unknown;
  decoratorShipDate: string | null;
  decoratorShippingAccountId: string | null;
  decoratorShippingMethod: string | null;
  decoratorNotes: string | null;
}

/** The order-level fallbacks the supplier PO uses today (order ship-to + in-hands). */
export interface SupplierLegFallback {
  fallbackSnapshot: unknown;
  fallbackAddressId: string | null;
  /** The merch'd warehouse ship-to snapshot (same shape as a client address
   *  snapshot) so a `merchd` routing physically ships to merch'd — NOT the
   *  client fallback (that was a misroute, ux B4). Required. */
  merchdSnapshot: unknown;
}

/** The supplier (product_supply) PO header fields derive pre-populates. When
 *  usedStaging is false the caller MUST keep its own fallback values so an
 *  unstaged order derives byte-for-byte as before. */
export interface SupplierLegDerivation {
  usedStaging: boolean;
  routing: PoShipRouting | null;
  /** Resolved ship-to (staged supplier/decorator address, else the fallback). */
  shipToAddressSnapshot: unknown;
  shipToAddressId: string | null;
  shippingAccountId: string | null;
  shippingMethod: string | null;
  supplierShipDate: string | null;
  notes: string | null;
  /** Non-null when ≥2 staged rows in the group disagree on a pre-populated field
   *  — appended to the PO's internal_notes so the rep can reconcile. */
  conflictNote: string | null;
  /** True when the winner routes to a decorator AND names one — the caller then
   *  mints the second decoration_service PO (T09). */
  hasDecoratorLeg: boolean;
  /** The winning staged row (first by position) — drives the decorator PO. */
  winner: StagedShippingRow | null;
}

/** The pre-populated fields (labelled) whose disagreement across a group's
 *  staged rows constitutes a conflict. Order is stable so the note is
 *  deterministic. */
const CONFLICT_FIELDS: Array<{
  label: string;
  of: (r: StagedShippingRow) => unknown;
}> = [
  { label: "routing", of: (r) => r.shipToRouting },
  { label: "ship-to address", of: (r) => r.supplierShipToAddressId },
  { label: "shipping account", of: (r) => r.supplierShippingAccountId },
  { label: "shipping method", of: (r) => r.supplierShippingMethod },
  { label: "ship date", of: (r) => r.supplierShipDate },
  { label: "decorator", of: (r) => r.decoratorVendorId },
  { label: "final ship-to", of: (r) => r.decoratorFinalShipTo },
  { label: "decorator ship date", of: (r) => r.decoratorShipDate },
];

/** norm — treat null / undefined / "" as the same "unset" value so an empty
 *  string and a null don't read as a conflict. */
function norm(v: unknown): string {
  return v == null || v === "" ? "" : String(v);
}

/**
 * Resolve the supplier PO header from a vendor group's staged shipping rows.
 *
 * @param stagedRows the group's staged rows (any order — sorted here by
 *   position). Rows are ONLY the lines that actually have a staged row; an
 *   unstaged line contributes nothing.
 * @param fallback the order-level ship-to the PO uses when unstaged.
 */
export function resolveSupplierPoShipping(
  stagedRows: StagedShippingRow[],
  fallback: SupplierLegFallback,
): SupplierLegDerivation {
  const base: SupplierLegDerivation = {
    usedStaging: false,
    routing: null,
    shipToAddressSnapshot: fallback.fallbackSnapshot,
    shipToAddressId: fallback.fallbackAddressId,
    shippingAccountId: null,
    shippingMethod: null,
    supplierShipDate: null,
    notes: null,
    conflictNote: null,
    hasDecoratorLeg: false,
    winner: null,
  };
  if (stagedRows.length === 0) return base;

  const sorted = [...stagedRows].sort((a, b) => a.position - b.position);
  const winner = sorted[0]!;

  // Resolve the supplier ship-to by the winner's routing:
  //   client        → the staged client ship-to (else the order fallback)
  //   decorator     → the decorator's own address (the supplier ships blanks there)
  //   merchd        → the merch'd warehouse (NOT the client fallback — ux B4)
  //   other_supplier→ the order fallback (the destination lives in Supplier notes;
  //                   the modal copy is honest about that — no address promise)
  let shipToAddressSnapshot: unknown = fallback.fallbackSnapshot;
  let shipToAddressId: string | null = fallback.fallbackAddressId;
  if (winner.shipToRouting === "client") {
    shipToAddressSnapshot =
      winner.supplierShipToAddressSnapshot ?? fallback.fallbackSnapshot;
    shipToAddressId =
      winner.supplierShipToAddressId ?? fallback.fallbackAddressId;
  } else if (winner.shipToRouting === "decorator") {
    shipToAddressSnapshot =
      winner.decoratorAddressSnapshot ?? fallback.fallbackSnapshot;
    shipToAddressId = winner.decoratorAddressId ?? null;
  } else if (winner.shipToRouting === "merchd") {
    // Physically ship to merch'd — the modal promises "the merch'd warehouse".
    shipToAddressSnapshot = fallback.merchdSnapshot;
    shipToAddressId = null;
  }

  // Conflict detection — which pre-populated fields differ across staged rows.
  const conflicting = CONFLICT_FIELDS.filter(({ of }) => {
    const w = norm(of(winner));
    return sorted.some((r) => norm(of(r)) !== w);
  }).map((f) => f.label);
  const conflictNote =
    conflicting.length > 0
      ? `Shipping staged differently across ${sorted.length} lines in this PO. ` +
        `Applied the first line's settings (by position). ` +
        `Differing: ${conflicting.join(", ")}. Review before sending.`
      : null;

  return {
    usedStaging: true,
    routing: winner.shipToRouting,
    shipToAddressSnapshot,
    shipToAddressId,
    shippingAccountId: winner.supplierShippingAccountId,
    shippingMethod: winner.supplierShippingMethod,
    supplierShipDate: winner.supplierShipDate,
    notes: winner.supplierNotes,
    conflictNote,
    hasDecoratorLeg:
      winner.shipToRouting === "decorator" && winner.decoratorVendorId != null,
    winner,
  };
}

/**
 * Send-time soft gate (D2): a decoration_service PO with $0 pricing is honest
 * only if the rep confirms — the decoration charges haven't been derived yet, so
 * the document would tell the decorator the work is free. Mirrors the round-47
 * zero-cost grammar: the CLIENT shows a confirm ("Decoration pricing isn't
 * included yet — send anyway?"), the SERVER 409s unless an audited skip flag is
 * passed, and the offline gate executes the SAME decider. Reps can always send.
 */
export function isDecorationPricingMissing(po: {
  poType: string;
  subtotalCost: number | string | null | undefined;
}): boolean {
  if (po.poType !== "decoration_service") return false;
  const n =
    typeof po.subtotalCost === "number"
      ? po.subtotalCost
      : Number(po.subtotalCost);
  return !Number.isFinite(n) || n <= 0;
}

/** One decoration's rendered scope input (per location). */
export interface DecorationScopeLine {
  productName: string;
  quantity: number;
  location: string | null;
  /** The imprint method/type label (free-text imprint_type, else the FK id). */
  imprintType: string | null;
  colorCount: number | null;
  inkColors: string[] | null;
  logoName: string | null;
  /** true → the logo repeats (repeat_logo is free text "yes"/"Y"/…). */
  repeatLogo: boolean;
  /** A logo file is attached (logo_file_id present) — the decorator has art. */
  hasLogoFile: boolean;
}

/**
 * Render a decorator-facing decoration SCOPE block for a decoration_service PO.
 *
 * The decoration PO is header-only (it carries no po_line_items so the
 * one-PO-per-line coverage invariant stays exact), so the actual work — which
 * items, how many, which locations, imprint method, colors, logo — must ride in
 * the PO's notes as human-readable instructions. Cost is intentionally absent
 * (a later round derives decoration charges). Returns null when there's nothing
 * to render (the caller then omits the block).
 *
 * Pure so the builder AND the offline gate render the SAME text.
 */
export const DECORATION_SCOPE_HEADING = "Decoration instructions:";

/** Notes are only what the rep typed for the vendor (Stephanie 2026-10-14).
 *  Decorator POs made before then had an auto-written "Decoration scope
 *  (pricing arrives in a later round):" block at the top of their notes —
 *  strip it (heading + its bullet lines) wherever notes are shown. The same
 *  information now shows in the PO body. Returns null when nothing is left. */
export function cleanPoNotes(notes: string | null | undefined): string | null {
  if (notes == null) return null;
  const cleaned = notes
    .replace(/^Decoration scope \(pricing arrives in a later round\):\n(?:• [^\n]*(?:\n|$))*/m, "")
    .trim();
  return cleaned.length > 0 ? cleaned : null;
}

export function composeDecorationScope(
  lines: DecorationScopeLine[],
): string | null {
  const rows = lines
    .map((l) => {
      const parts: string[] = [];
      if (l.location) parts.push(l.location);
      if (l.imprintType) parts.push(l.imprintType);
      if (l.colorCount != null) {
        parts.push(`${l.colorCount} color${l.colorCount === 1 ? "" : "s"}`);
      }
      const inks = (l.inkColors ?? []).filter((c) => c && c.trim());
      if (inks.length > 0) parts.push(inks.join("/"));
      if (l.logoName) parts.push(`logo "${l.logoName}"`);
      if (l.repeatLogo) parts.push("repeats");
      if (!l.hasLogoFile) parts.push("art to follow");
      const detail = parts.length > 0 ? parts.join(", ") : "decoration";
      return `• ${l.productName} ×${l.quantity} — ${detail}`;
    })
    .filter((s) => s.length > 0);
  if (rows.length === 0) return null;
  // Never "pricing arrives later" on a PO the decorator reads (Stephanie
  // 2026-10-14) — the charges are on the PO's lines.
  return DECORATION_SCOPE_HEADING + "\n" + rows.join("\n");
}
